import { useMemo } from 'react'

import { InfiniteData, QueryKey, useInfiniteQuery } from '@tanstack/react-query'

import { GetShortVideoListViewModelPaginationViewModel } from '@/types/cml-api'
import { NextSearchParams } from '@/types/general'

import { InteractifyQueryKeys } from '@/features/interactify/types'

import fetchRequest from '@/app/_utils/fetch-request'

import { generateSearchParams } from '@/utils/generate-search-params'

import { PAGE_SIZE } from '../constants'
import { ApiParamsKeys } from '../types'

interface Props {
    searchParams?: NextSearchParams
    enabled?: boolean
    slug?: string
    productSlug?: string
    filteredSlugs?: string[]
}

const useGetVideos = ({ searchParams = {}, slug, productSlug, enabled = true, filteredSlugs = [] }: Props) => {
    const requestParams: NextSearchParams = {}

    Object.values(ApiParamsKeys).forEach((key) => {
        if (searchParams?.[key]) {
            requestParams[key] = searchParams[key]
        }
    })

    // set page size based on the page that request is sent from
    requestParams[ApiParamsKeys.PageSize] = PAGE_SIZE.VideosLanding
    if (slug) {
        requestParams[ApiParamsKeys.Slug] = slug
        requestParams[ApiParamsKeys.PageSize] = PAGE_SIZE.HomeLanding
    }
    if (productSlug) {
        requestParams[ApiParamsKeys.ProductSlug] = productSlug
        requestParams[ApiParamsKeys.PageSize] = PAGE_SIZE.VideoPDP
    }

    const { data, isRefetching, fetchNextPage, isFetchingNextPage, isFetching, isLoading, hasNextPage } =
        useInfiniteQuery<
            GetShortVideoListViewModelPaginationViewModel,
            unknown,
            InfiniteData<GetShortVideoListViewModelPaginationViewModel>,
            QueryKey,
            number
        >({
            queryKey: [InteractifyQueryKeys.ShortsList, requestParams],
            staleTime: Infinity,

            queryFn: ({ pageParam = 1 }) => {
                const params = generateSearchParams(requestParams)

                params.set(ApiParamsKeys.PageNumber, pageParam.toString())

                return fetchRequest({
                    endpoint: `/videos/short-video?${params.toString()}`,
                    method: 'GET',
                    serviceName: 'cml',
                    appendCurrentQueryStrings: false,
                })
            },
            select: (data) => {
                if (!filteredSlugs.length) return data

                return {
                    ...data,
                    pages: data.pages.map((page) => {
                        return {
                            ...page,
                            items: page.items?.filter((item) => !filteredSlugs.includes(item.slug || '')),
                        }
                    }),
                }
            },
            getNextPageParam: (lastPage) => {
                const { totalCount = 0, pageSize = PAGE_SIZE, pageNumber = 0 } = lastPage || {}

                const pageCount = Math.ceil(totalCount / +pageSize)

                return pageNumber >= pageCount ? undefined : pageNumber + 1
            },
            refetchOnMount: false,
            initialPageParam: 1,
            enabled,
        })

    const videos = useMemo(() => data?.pages.flatMap((page) => page.items || []) ?? [], [data])

    return {
        videos,
        fetchNextPage,
        isLoading,
        isFetching,
        isRefetching,
        isFetchingNextPage,
        hasNextPage,
    }
}

export default useGetVideos
